<template>
  <div
    :class="producerClasses"
    :data-cube="this.producer.cubeName"
    :id="`p_${this.producer.cubeName}`"
  >
    <a class="producer__left" @click="showProducerInMap()">
      <img
        class="producer__img"
        :src="$store.getters.cubeIconUrl(this.producer, 'svg')"
      />
      <h4 class="producer__title">{{ nickName }}</h4>
      <span
        v-if="unreadNotifications.length > 0"
        class="producer__has-notification"
        :title="str('notifications.title')"
        @click.capture.stop="showNotifications = !showNotifications"
      ></span>
    </a>
    <div class="producer__right">
      <div class="producer__content">
        <div class="producer__content-top">
          <div class="producer__priority">
            <div class="producer__priority-title">Priorität</div>
            <Choice
              name="priority"
              :default="priority"
              :items="priorities"
              @selection-changed="onPriorityChanged"
            />
          </div>
        </div>
        <a
          :class="toggleActionsClasses"
          @click.stop="toggleActions()"
          title="mehr"
        >
          <IconVerticalEllipsis />

          <div :class="actionsClasses" @click.stop="">
            <a :class="disconnectLinkClasses" @click="disconnectFromProducer()">
              <IconTimes />
              Verbindung trennen
            </a>
          </div>
        </a>
        <div class="producer__content-bottom">
          <output class="producer__current-price">
            <Price
              :label="str('efriends.currentPrice')"
              :value="currentPrice"
            />
          </output>
          <output class="producer__next-price" v-if="nextPrice">
            <Price :label="nextPriceLabel" :value="nextPrice" />
          </output>
        </div>

        <div class="producer__fields">
          <div class="producer__capacity">
            <div class="producer__label">Leistung</div>
            <div class="producer__value">
              {{ formatKWP(this.producer.params.productionCapacityKWp * 1000) }}
            </div>
          </div>

          <div class="producer__energy-lines">
            <div class="producer__label">verwendet</div>
            <div class="producer__value">
              {{ this.producer.params.energyLinesRequired }}
              <span class="producer__value-subtitle">
                Energyline{{
                  this.producer.params.energyLinesRequired > 1 ? 's' : ''
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div :class="submitClasses">
        <a class="producer__cancel" @click="cancel()">abbrechen</a>

        <LinkButton
          type="small"
          icon-right="Arrow"
          :extra-classes="saveButtonClasses"
          :text="str('global.save')"
          @click.native="saveChanges()"
        />
      </div>
    </div>
    <CubeNotifications
      v-if="showNotifications"
      :notifications="unreadNotifications"
      :cube-name="producer.cubeName"
      :extra-classes="['producer__notifications']"
    />
  </div>
</template>

<script>
  import IconVerticalEllipsis from '@/components/icons/VerticalEllipsis'
  import IconTimes from '@/components/icons/Times'
  import Choice from '@/components/Choice'
  import Price from '@/components/Price'
  import LinkButton from '@/components/LinkButton'
  import util from '@/js/Util'
  import CubeNotifications from '@/components/efriends/CubeNotifications'

  export default {
    name: 'producer',
    props: {
      producer: {
        type: Object,
        required: true,
      },
    },
    components: {
      IconVerticalEllipsis,
      IconTimes,
      Choice,
      Price,
      LinkButton,
      CubeNotifications,
    },
    data() {
      return {
        showActions: false,
        disconnecting: false,
        originalPriority: false,
        priority: false,
        currentPrice: false,
        nextPrice: false,
        bodyOnClickHandler: false,
        showNotifications: false,
      }
    },
    created() {
      this.initializeValues()
    },
    computed: {
      producerClasses() {
        let result = ['producer']
        const cubeNameRegex = new RegExp(`p_${this.producer.cubeName}`)
        if (
          this.$route.name === 'efriends' &&
          this.$route.hash.match(cubeNameRegex)
        ) {
          result.push('producer--highlighted')
        }
        return result.join(' ')
      },
      priorities: function () {
        return this.$store.getters.connectionPriorities
      },
      actionsClasses: function () {
        let result = ['producer__actions']
        if (this.showActions) {
          result.push('producer__actions--visible')
        }
        return result.join(' ')
      },
      toggleActionsClasses: function () {
        let result = ['producer__toggle-actions']
        if (this.showActions) {
          result.push('producer__toggle-actions--active')
        }
        return result.join(' ')
      },
      disconnectLinkClasses: function () {
        let classes = ['producer__action-disconnect']
        if (this.disconnecting) {
          classes.push('producer__action-disconnect--disconnecting')
        }
        return classes.join(' ')
      },
      nickName: function () {
        return this.producer.params.nickName
      },
      nextPriceLabel: function () {
        return `Ab ${util.formatPriceDate(this.dateNextPrice)}`
      },
      submitClasses: function () {
        let classes = ['producer__submit-buttons']
        if (this.isChanged()) {
          classes.push('producer__submit-buttons--expanded')
        }
        return classes.join(' ')
      },
      saveButtonClasses: function () {
        let classes = ['button--primary producer__save']
        if (
          this.$store.getters.isFormSubmitting(
            `producers.${this.producer.cubeName}`
          )
        ) {
          classes.push('producer__actions--submitting')
        }
        return classes
      },
      pricesFromStore: function () {
        return this.$store.getters.producerPrices(this.producer.cubeName)
      },
      unreadNotifications: function () {
        return this.$store.getters
          .notificationsForCube(this.producer.cubeName)
          .filter((notification) => {
            return notification.unread === 1
          })
      },
    },
    methods: {
      initializeValues: function () {
        this.priority = this.$store.getters.connectionPriority(this.producer)
        this.originalPriority = this.priority
        const prices = this.$store.getters.producerPrices(
          this.producer.cubeName
        )
        if (prices) {
          if (typeof prices.currentPrice !== 'undefined') {
            this.currentPrice = prices.currentPrice
          }

          if (typeof prices.nextPrice !== undefined) {
            this.nextPrice = prices.nextPrice
            this.dateNextPrice = prices.dateNextPrice
          }
        }
      },
      toggleActions: function () {
        this.showActions = !this.showActions
        if (this.showActions) {
          this.bodyOnClickHandler = this.hideActions.bind(this)
          document.body.addEventListener('click', this.bodyOnClickHandler)
        } else {
          document.body.removeEventListener('click', this.bodyOnClickHandler)
        }
      },
      hideActions: function () {
        this.showActions = false
        document.body.removeEventListener('click', this.bodyOnClickHandler)
      },
      disconnectFromProducer() {
        this.disconnecting = true
        this.$store.dispatch('disconnectFromCube', this.producer)
      },
      onPriorityChanged: function (newPriority) {
        this.priority = newPriority
      },
      isChanged: function () {
        return this.priority !== this.originalPriority
      },
      cancel: function () {
        this.priority = this.originalPriority
      },
      saveChanges: async function () {
        this.$store.dispatch('setConnectionPriority', {
          cubeName: this.producer.cubeName,
          priority: this.priority,
          forConsumers: false,
        })
        this.originalPriority = this.priority
      },
      showProducerInMap: function () {
        this.$router.push({
          name: 'map',
          params: {
            selected: this.producer.cubeName,
          },
        })
      },
    },
    watch: {
      pricesFromStore: function (newPrices) {
        if (newPrices) {
          if (typeof newPrices.currentPrice !== 'undefined') {
            this.currentPrice = newPrices.currentPrice
          }

          if (typeof newPrices.nextPrice !== 'undefined') {
            this.nextPrice = newPrices.nextPrice
            this.dateNextPrice = newPrices.dateNextPrice
          }
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  $producer-padding-top: spacer(1);

  .producer {
    display: flex;
    flex-wrap: wrap;
    padding: $producer-padding-top 0;
    position: relative;

    &--highlighted {
      background: $grey-lightest;
    }

    &__left {
      margin-right: spacer(1);
      text-align: center;
      transition: $transition-base;
      flex-shrink: 0;
      width: $cube-list-img-size-min;
      cursor: pointer;
      position: relative;

      @media (min-width: $bp-xs) {
        width: $cube-list-img-size-xs;
      }

      @media (min-width: $bp-sm) {
        flex-basis: $efriends-page-icon-width-sm;
      }
    }

    &__img {
      padding: 0;
      max-width: 100%;

      @media (min-width: $bp-ssm) {
        padding: 0 spacer(0.5);
      }

      @media (min-width: $bp-sm) {
        padding: 0 spacer(2);
      }
    }

    &__right {
      flex-grow: 1;
      margin-right: spacer(2);

      @media (min-width: $bp-md) {
        margin-right: spacer(3);
        // Only when setting a width that is smaller than the available space
        // this will fit onto the same line as left.
        width: 30%;
      }
    }

    &__title {
      margin: 0;
      text-align: center;
      font-size: $font-size-sm;
      line-height: $line-height-sm;
      word-break: break-all;
      transition: $transition-base;
      font-weight: normal;

      @media (min-width: $bp-ssm) {
        font-size: $font-size-base;
      }
    }

    &__has-notification {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 0.75em;
      height: 0.75em;
      top: spacer(0.1) * -1;
      left: spacer(0.1) * -1;
      position: absolute;
      background: $notification-color;
      padding: 0.25em;
      border-radius: 100%;
      color: $white;

      @media (min-width: $bp-sm) {
        top: spacer(0.5);
        left: spacer(1);
        width: 1em;
        height: 1em;
      }
    }

    &__fields {
      display: flex;
      flex-wrap: wrap;
      margin-top: spacer(0.25);

      @media (min-width: $bp-md) {
        justify-content: flex-end;
        flex-basis: 100%;
      }
    }

    &__label {
      font-size: $font-size-sm;
      color: $grey-dark;

      @media (min-width: $bp-xs) {
        font-size: $font-size-base;
      }
    }

    &__value {
      line-height: $line-height-sm;
      font-size: $font-size-base;

      @media (min-width: $bp-sm) {
        font-size: $font-size-lg;
      }

      @media (min-width: $bp-md) {
        font-size: $font-size-xl;
        margin-top: spacer(0.1);
      }

      &-subtitle {
        @media (max-width: $bp-xs - 1px) {
          font-size: 80%;
        }
      }
    }

    &__capacity,
    &__energy-lines {
      font-size: $font-size-base;
      line-height: $line-height-sm;
      margin-bottom: spacer(0.25);
      transition: $transition-base;
      flex-basis: $price-min-width-min;

      @media (min-width: $bp-xs) {
        font-size: $font-size-lg;
        margin-bottom: 0;
        flex-basis: $price-min-width-xs;
      }

      @media (min-width: $bp-ssm) {
        flex-basis: $price-min-width-ssm;
      }

      @media (min-width: $bp-md) {
        font-size: $font-size-xl;
        margin-top: spacer(0.1);
      }
    }

    &__toggle-actions {
      color: $grey-light;
      cursor: pointer;
      transition: $transition-base;
      width: spacer(2);
      padding: 0 spacer(0.5);
      align-self: flex-end;
      position: absolute;
      top: $producer-padding-top + 1.15rem;
      right: 0;

      @include hover-focus-active {
        color: $community;
      }

      &--active {
        color: $community;
      }

      .icon {
        width: spacer(0.5);
      }
    }

    &__actions {
      position: absolute;
      right: 0;
      top: 100%;
      left: auto;
      overflow: hidden;
      transition: opacity 0.3s ease 0s, visibility 0s linear 0.5s;
      padding: spacer(0.5) spacer(1);
      border: 1px solid $grey-light;
      border-radius: $border-radius;
      opacity: 0;
      width: 200px;
      background: $white;
      visibility: hidden;

      @media (min-width: $bp-sm) {
        right: spacer(0.5);
      }

      &--visible {
        visibility: visible;
        transition: visibility 0s linear 0s, opacity 0.3s ease 0.1s;
        opacity: 1;
        box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.1);
      }

      &-overlay {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
      }
    }

    &__action-disconnect {
      cursor: pointer;
      transition: $transition-base;

      @include hover-focus-active {
        color: $error-color;
      }

      .icon {
        margin-right: spacer(0.25);
        width: 0.7rem;
        vertical-align: middle;
      }

      &--disconnecting {
        .icon {
          animation: spin 2s linear 0s infinite;
        }
      }
    }

    &__priority {
      margin-right: spacer(0.5);

      &-title {
        font-size: $font-size-sm;
        line-height: $line-height-sm;
        color: $grey-dark;
      }
    }

    &__content {
      flex-grow: 1;

      @media (min-width: $bp-md) {
        display: flex;
        flex-wrap: wrap;
      }

      &-top {
        display: flex;
        flex-wrap: wrap;
      }

      &-bottom {
        margin-top: spacer(1);
        display: flex;
        justify-self: flex-end;
        margin-left: auto;
        flex-grow: 1;
        flex-basis: 40%;

        @media (min-width: $bp-md) {
          margin-top: 0;
          justify-content: flex-end;
        }
      }
    }

    &__submit-buttons {
      max-height: 0;
      overflow: hidden;
      transition: $transition-base;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: spacer(0.5);
      // The __right element has a margin-right for producers to accomodate for
      // the toggle actions button. We still want the submit buttons to be at
      // the very right though.
      margin-right: spacer(2) * -1;

      @media (min-width: $bp-md) {
        justify-content: flex-end;
        flex-basis: 100%;
      }

      &--expanded {
        max-height: 60px;
      }
    }

    &__cancel {
      cursor: pointer;
      transition: $transition-base;
    }

    &__save {
      align-self: flex-end;
      margin-left: spacer(1);
    }
  }
</style>
